import { ReactElement } from "react";

import DropdownMenuItem from "ds/components/DropdownMenu/Item";

import TableHeadInteractiveColumnOptionsDropdown from "./Options";
import { COLUMN_CHANGED_ANALYTIC_EVENT } from "./constants";
import { TableHeadInteractiveColumnProps } from ".";
import { TableColumnsState } from "../../store/useTableStore/types";

type TableHeadInteractiveColumnActionsDropdownProps = Pick<
  TableHeadInteractiveColumnProps,
  "column" | "stickyLeft" | "analyticsPage" | "isPinningEnabled"
> &
  Pick<TableColumnsState, "columnsOrder" | "hideColumn" | "pinColumn" | "reorderVisibleColumns">;

const TableHeadInteractiveColumnActionsDropdown = ({
  column,
  columnsOrder,
  stickyLeft,
  hideColumn,
  pinColumn,
  reorderVisibleColumns,
  analyticsPage,
  isPinningEnabled,
}: TableHeadInteractiveColumnActionsDropdownProps): ReactElement | null => {
  const showHideAction = column.canHide;
  const showPinAction = column.canPin;
  const showMoveActions = !!reorderVisibleColumns && column.canMove;

  if (!showHideAction && !showPinAction && !showMoveActions) {
    return null;
  }

  const isFirstColumn = columnsOrder.visible.indexOf(column.id) === 0;
  const canMoveLeft = columnsOrder.visible.indexOf(column.id) > 0;
  const canMoveRight = columnsOrder.visible.indexOf(column.id) < columnsOrder.visible.length - 2;

  const getPinActionText = () => {
    if (!isFirstColumn) return "Pin to start";
    if (stickyLeft) return "Unpin";
    return "Pin";
  };

  return (
    <TableHeadInteractiveColumnOptionsDropdown>
      {showPinAction && (
        <DropdownMenuItem
          onAction={() => pinColumn(column.id)}
          isDisabled={isPinningEnabled === false}
          tooltip={isPinningEnabled === false ? "Table is too narrow to pin columns" : undefined}
          analyticsPage={analyticsPage}
          analyticsTitle={COLUMN_CHANGED_ANALYTIC_EVENT}
          analyticsProps={{
            column: column.id,
            action: stickyLeft ? "unpin" : "pin",
          }}
        >
          {getPinActionText()}
        </DropdownMenuItem>
      )}

      {showHideAction && (
        <DropdownMenuItem
          onAction={() => hideColumn(column.id)}
          analyticsPage={analyticsPage}
          analyticsTitle={COLUMN_CHANGED_ANALYTIC_EVENT}
          analyticsProps={{ column: column.id, action: "hide" }}
        >
          Hide
        </DropdownMenuItem>
      )}

      {showMoveActions && (
        <>
          <DropdownMenuItem
            onAction={() => reorderVisibleColumns(column.id, "left")}
            isDisabled={!canMoveLeft}
            analyticsPage={analyticsPage}
            analyticsTitle={COLUMN_CHANGED_ANALYTIC_EVENT}
            analyticsProps={{ column: column.id, action: "move left" }}
          >
            Move left
          </DropdownMenuItem>
          <DropdownMenuItem
            onAction={() => reorderVisibleColumns(column.id, "right")}
            isDisabled={!canMoveRight}
            analyticsPage={analyticsPage}
            analyticsTitle={COLUMN_CHANGED_ANALYTIC_EVENT}
            analyticsProps={{ column: column.id, action: "move right" }}
          >
            Move right
          </DropdownMenuItem>
        </>
      )}
    </TableHeadInteractiveColumnOptionsDropdown>
  );
};

TableHeadInteractiveColumnActionsDropdown.displayName =
  "DS.Table.Head.InteractiveColumn.ActionsDropdown";

export default TableHeadInteractiveColumnActionsDropdown;
